import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../components/DataTable';
import type { Column } from '../components/DataTable';
import { fetchEmployers, disableAccount, resetPassword, type AdminEmployer } from '../lib/userService';
import { logger } from '../lib/logger';

const Employers: React.FC = () => {
  const navigate = useNavigate();
  const [employers, setEmployers] = useState<AdminEmployer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [subFilter, setSubFilter] = useState<string>('all');
  const [seedFilter, setSeedFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [resetModal, setResetModal] = useState<AdminEmployer | null>(null);
  const [newPwd, setNewPwd] = useState('');

  useEffect(() => {
    fetchEmployers().then(setEmployers).catch((err) => logger.error('load_employers', 'Employers', { error: String(err) })).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return employers.filter((e) => {
      const matchSearch =
        search === '' ||
        e.companyName.toLowerCase().includes(search.toLowerCase()) ||
        `${e.firstName} ${e.lastName}`.toLowerCase().includes(search.toLowerCase()) ||
        e.siret.includes(search);
      const matchSub = subFilter === 'all' ||
        (subFilter === 'premium' && e.subscriptionActive) ||
        (subFilter === 'free' && !e.subscriptionActive);
      const matchSeed = seedFilter === 'all' || (seedFilter === 'seed' ? !!e.seedTag : !e.seedTag);
      return matchSearch && matchSub && matchSeed;
    });
  }, [employers, search, subFilter, seedFilter]);

  const handleToggleSuspend = async (e: AdminEmployer) => {
    setActionLoading(e.userId);
    try {
      await disableAccount(e.userId, true);
      setEmployers((prev) =>
        prev.map((x) => (x.userId === e.userId ? { ...x } : x))
      );
      alert('Compte suspendu.');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleResetPwd = async () => {
    if (!resetModal || !newPwd) return;
    setActionLoading(resetModal.userId);
    try {
      await resetPassword(resetModal.userId, newPwd);
      alert('Mot de passe réinitialisé.');
      setResetModal(null);
      setNewPwd('');
    } catch (err) {
      alert(`Erreur: ${err}`);
    } finally {
      setActionLoading(null);
    }
  };

  const columns: Column<AdminEmployer>[] = [
    {
      key: 'company',
      header: 'Entreprise',
      render: (e) => (
        <div>
          <div className="flex items-center gap-1.5">
            <p className="font-medium text-gray-900">{e.companyName}</p>
            {e.seedTag && <span className="px-1.5 py-0.5 text-[9px] font-bold rounded bg-indigo-100 text-indigo-700 uppercase">seed</span>}
          </div>
          <p className="text-xs text-gray-400">{e.city || '—'}</p>
        </div>
      ),
    },
    {
      key: 'siret',
      header: 'SIRET',
      render: (e) => (
        <div className="flex items-center gap-2">
          <span className="text-gray-600 font-mono text-xs">{e.siret || '—'}</span>
          {e.siretVerified && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-semibold rounded-full border border-blue-200">
              ✓ Vérifié
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contact',
      render: (e) => (
        <div>
          <p className="text-gray-700">{e.firstName} {e.lastName}</p>
          <p className="text-xs text-gray-400">{e.email}</p>
        </div>
      ),
    },
    {
      key: 'subscription',
      header: 'Abonnement',
      render: (e) => (
        <span
          className={`inline-block px-2.5 py-1 text-xs font-semibold rounded-full ${
            e.subscriptionActive
              ? 'bg-gradient-to-r from-amber-50 to-yellow-50 text-amber-700 border border-amber-200'
              : 'bg-gray-100 text-gray-500 border border-gray-200'
          }`}
        >
          {e.subscriptionActive ? '★ Premium' : 'Gratuit'}
        </span>
      ),
    },
    {
      key: 'offers',
      header: 'Offres',
      render: (e) => (
        <span className="inline-block px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded-md">
          {e.jobOffersCount} offre{e.jobOffersCount > 1 ? 's' : ''}
        </span>
      ),
    },
    {
      key: 'registeredAt',
      header: 'Inscrit le',
      render: (e) => <span className="text-gray-500">{e.registeredAt}</span>,
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (e) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(ev) => { ev.stopPropagation(); handleToggleSuspend(e); }}
            disabled={actionLoading === e.userId}
            className="px-2.5 py-1 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-md transition-colors disabled:opacity-40"
          >
            Suspendre
          </button>
          <button
            onClick={(ev) => { ev.stopPropagation(); setResetModal(e); setNewPwd(''); }}
            className="px-2.5 py-1 text-xs font-medium text-[#00244F] bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
          >
            Mdp
          </button>
        </div>
      ),
    },
  ];

  const filterSlot = (
    <div className="flex items-center gap-2">
      <select
        value={subFilter}
        onChange={(e) => setSubFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
      >
        <option value="all">Tous les abonnements</option>
        <option value="premium">Premium</option>
        <option value="free">Gratuit</option>
      </select>
      <select
        value={seedFilter}
        onChange={(e) => setSeedFilter(e.target.value)}
        className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
      >
        <option value="all">Tous (réels + seeds)</option>
        <option value="real">Réels uniquement</option>
        <option value="seed">Seeds uniquement</option>
      </select>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-[#00244F]/20 border-t-[#00244F] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="bg-white rounded-xl border border-gray-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">🏢</span>
          <div>
            <p className="text-xl font-bold text-gray-900">{employers.length}</p>
            <p className="text-xs text-gray-500">Total recruteurs</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-amber-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">★</span>
          <div>
            <p className="text-xl font-bold text-amber-600">{employers.filter(e => e.subscriptionActive).length}</p>
            <p className="text-xs text-gray-500">Premium</p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-blue-200 px-4 py-3 flex items-center gap-3">
          <span className="text-2xl">✓</span>
          <div>
            <p className="text-xl font-bold text-blue-600">{employers.filter(e => e.siretVerified).length}</p>
            <p className="text-xs text-gray-500">SIRET vérifié</p>
          </div>
        </div>
      </div>

      <DataTable
        columns={columns}
        onRowClick={(e) => navigate(`/employers/${e.id}`)}
        data={filtered}
        keyExtractor={(e) => e.id}
        searchable
        searchPlaceholder="Rechercher par entreprise, contact, SIRET..."
        onSearch={setSearch}
        filterSlot={filterSlot}
        pageSize={10}
      />

      {resetModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50" onClick={() => setResetModal(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">Réinitialiser le mot de passe</h3>
              <p className="text-sm text-gray-500 mt-1">{resetModal.companyName} ({resetModal.email})</p>
            </div>
            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">Nouveau mot de passe</label>
              <input
                type="text"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Minimum 6 caractères"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00244F]/20 focus:border-[#00244F]"
                autoFocus
              />
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-end gap-3 bg-gray-50">
              <button onClick={() => setResetModal(null)} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">
                Annuler
              </button>
              <button
                onClick={handleResetPwd}
                disabled={newPwd.length < 6 || actionLoading === resetModal.userId}
                className="px-4 py-2 text-sm font-medium text-white bg-[#00244F] hover:bg-[#001B3D] rounded-lg transition-colors disabled:opacity-40"
              >
                Confirmer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employers;
